/* ============================================================================
 * useSyncUtilities.ts - Server synchronization utilities
 * ============================================================================ */

import { useCallback, useEffect, useRef, type MutableRefObject, type Dispatch, type SetStateAction } from 'react';
import type { WebSocketManager } from '@/shared/api/WebSocketManager';
import { API_BASE_URL, API_ENDPOINTS } from '@/shared/apiClient';
import { authService } from '@/features/auth/services/authService';
import { getAuthRuntimeSessionIdentity } from '@/features/auth/services/authRuntime';
import { getAdminViewAsUserId } from '@/features/auth/services/adminViewAs';
import { log } from '@/shared/utils/logger';
import type { SyncState } from '@/shared/apiTypes';
import {
  buildPendingOperationBatchPayload,
  acknowledgedOperationsFromResponse,
  applyAcknowledgedBatchToSyncState,
  PendingOperationProjectRoutingError,
} from './syncPendingOperations';

interface UseSyncUtilitiesParams {
  projectId?: string | null;
  reconnectProjectId?: string | null;
  projectIds?: string[];
  refreshProject?: (id: string, options?: { shouldApply?: () => boolean }) => Promise<void>;
  hasUnconfirmedWrites?: boolean;
  getWriteRevision?: () => number;
  // State
  syncState: SyncState;
  setSyncState: Dispatch<SetStateAction<SyncState>>;
  
  // Refs
  wsManager: MutableRefObject<WebSocketManager | null>;
  sessionId: MutableRefObject<string>;
  
  // Functions
  loadProjectDataFromServer: () => void;
}

/**
 * Hook for server synchronization utilities
 */
export function useSyncUtilities({
  projectId,
  reconnectProjectId = projectId,
  projectIds,
  refreshProject,
  hasUnconfirmedWrites = false,
  getWriteRevision,
  syncState,
  setSyncState,
  wsManager,
  sessionId,
  loadProjectDataFromServer
}: UseSyncUtilitiesParams) {
  const syncInFlightRef = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const owner = JSON.stringify([getAuthRuntimeSessionIdentity(), getAdminViewAsUserId()]);
  const scope = JSON.stringify([owner, [...new Set(projectIds ?? [])].sort()]);
  const epochs = useRef({ scope, selection: 0, writes: 0 });
  if (epochs.current.scope !== scope) {
    epochs.current.scope = scope;
    epochs.current.selection += 1;
  }
  if (hasUnconfirmedWrites || syncState.pendingOperations.length) epochs.current.writes += 1;
  const current = useRef({ scope, hasUnconfirmedWrites, syncState });
  current.current = { scope, hasUnconfirmedWrites, syncState };
  const retrySyncRecovery = useCallback(async () => {
    const manager = wsManager.current;
    if (manager?.getRecoveryState().status === 'conflict') {
      await manager.retryRecovery();
    }
  }, [wsManager]);
  
  // Check server version for sync
  const checkServerVersion = useCallback(async () => {
    try {
      if (!projectId) throw new Error('Missing project context for version check');
      const response = await authService.authenticatedFetch(
        `${API_BASE_URL}${API_ENDPOINTS.sync.version}?project_id=${encodeURIComponent(projectId)}`
      );
      
      if (response.ok) {
        const { version } = await response.json();
        if (typeof version !== 'number' || !Number.isFinite(version) || version < 0) {
          throw new Error('Invalid project version response');
        }
        
        setSyncState(prev => {
          const needsSync = version > prev.localVersion;
          return {
            ...prev,
            serverVersion: version,
            syncStatus: needsSync || prev.pendingOperations.length > 0 ? 'pending' : 'synced'
          };
        });
        
        return version;
      }
      throw new Error('Project version request failed');
    } catch (error) {
      log.error('useSyncUtilities', 'Version check failed:', error);
      setSyncState(prev => ({ ...prev, syncStatus: 'conflict' }));
    }
    return null;
  }, [projectId, setSyncState]);
  
  // Sync with server
  const syncWithServer = useCallback(async () => {
    if (syncInFlightRef.current) return;
    if (projectIds && refreshProject) {
      // A WebSocket version belongs to one project. It cannot represent an
      // aggregated selection. Read each visible project through its real loader.
      const targets = [...new Set(projectIds)].filter(id => id && id !== 'default');
      const selectionEpoch = epochs.current.selection;
      const writeEpoch = epochs.current.writes;
      const writeRevision = getWriteRevision?.();
      const sameScope = () => mounted.current && current.current.scope === scope &&
        epochs.current.selection === selectionEpoch &&
        JSON.stringify([getAuthRuntimeSessionIdentity(), getAdminViewAsUserId()]) === owner;
      const canRead = () => {
        const state = current.current;
        const managerState = wsManager.current?.getSyncState();
        return sameScope() && epochs.current.writes === writeEpoch && getWriteRevision?.() === writeRevision &&
          !state.hasUnconfirmedWrites && !state.syncState.pendingOperations.length &&
          !managerState?.pendingOperations.length && managerState?.syncStatus !== 'conflict';
      };
      if (!targets.length || !canRead()) return;
      syncInFlightRef.current = true;
      setSyncState(prev => ({ ...prev, syncStatus: 'syncing' }));
      try {
        // Keep the single-flight lock until every read settles, even on failure.
        const results = await Promise.allSettled(targets.map(id => refreshProject(id, { shouldApply: canRead })));
        if (!sameScope()) return;
        const failed = results.some(result => result.status === 'rejected');
        setSyncState(prev => {
          if (!sameScope() || prev.syncStatus === 'conflict') return prev;
          const managerState = wsManager.current?.getSyncState();
          return { ...prev,
            syncStatus: failed || managerState?.syncStatus === 'conflict' ? 'conflict'
              : !canRead() ? 'pending' : 'synced',
            lastSyncTime: failed || !canRead() ? prev.lastSyncTime : Date.now(),
          };
        });
      } finally {
        syncInFlightRef.current = false;
      }
      return;
    }
    syncInFlightRef.current = true;
    setSyncState(prev => ({ ...prev, syncStatus: 'syncing' }));

    try {
      // If we have pending operations, send them
      if (syncState.pendingOperations.length > 0) {
        const submitted = [...syncState.pendingOperations];
        let payload;
        try {
          payload = buildPendingOperationBatchPayload(
            submitted,
            syncState.localVersion,
          );
        } catch (error) {
          log.error('useSyncUtilities', 'Pending operations cannot be routed:', error);
          setSyncState(prev => ({ ...prev, syncStatus: 'conflict' }));
          if (!(error instanceof PendingOperationProjectRoutingError)) {
            throw error;
          }
          return;
        }

        const response = await authService.authenticatedFetch(
          `${API_BASE_URL}${API_ENDPOINTS.operations.batch}`,
          {
            method: 'POST',
            body: JSON.stringify(payload),
          }
        );

        if (response.ok) {
          const { acknowledged, hasFailures } = acknowledgedOperationsFromResponse(await response.json(), submitted);
          const lastSyncTime = Date.now();
          setSyncState(prev => applyAcknowledgedBatchToSyncState(
            prev,
            acknowledged,
            lastSyncTime,
            hasFailures,
          ));
          return;
        }

        log.error('useSyncUtilities', 'Batch sync HTTP failure:', {
          status: response.status,
          statusText: response.statusText,
        });
        setSyncState(prev => ({ ...prev, syncStatus: 'conflict' }));
        return;
      } else {
        // Just check for updates
        const serverVersion = await checkServerVersion();
        if (serverVersion !== null && serverVersion > syncState.localVersion) {
          await loadProjectDataFromServer();
        }
        setSyncState(prev => (
          prev.syncStatus === 'syncing'
            ? { ...prev, syncStatus: prev.pendingOperations.length > 0 ? 'pending' : 'synced' }
            : prev
        ));
      }
    } catch (error) {
      log.error('useSyncUtilities', 'Sync failed:', error);
      setSyncState(prev => ({ ...prev, syncStatus: 'conflict' }));
    } finally {
      syncInFlightRef.current = false;
    }
  }, [syncState, setSyncState, checkServerVersion, loadProjectDataFromServer, projectIds, refreshProject, scope, owner, wsManager, getWriteRevision]);
  
  // Reconnect WebSocket
  const reconnectWebSocket = useCallback(() => {
    log.info('useSyncUtilities', 'Attempting to reconnect WebSocket');
    const manager = wsManager.current;
    if (!manager) {
      log.error('useSyncUtilities', 'No WebSocket manager available');
      return;
    }
    const user = authService.getUser();
    if (!user?.id || !reconnectProjectId || !sessionId.current) {
      log.error('useSyncUtilities', 'Missing current user, project, or session for reconnection');
      return;
    }
    log.debug('useSyncUtilities', 'Reconnecting with:', { userId: user.id, projectId: reconnectProjectId });
    manager.connect(sessionId.current, user.id, reconnectProjectId);
  }, [reconnectProjectId, wsManager, sessionId]);

  return {
    checkServerVersion,
    syncWithServer,
    reconnectWebSocket,
    retrySyncRecovery
  };
}
